/**
 * Just For You My List Service V2
 */

import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { defer, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Services
//  import { AppConfigService } from '../../core/app.config.service';

// Data model
import { CouponModel } from '../models/data.model';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { ParamService } from './param.service';

@Injectable()
export class MyListService {
  public windowObj: any = window;
  public AB = this.windowObj['AB'];
  public enableBonusPathV2Flag: boolean = true;
  public myListHTMLProps: any;

  private currentTimeInMilliseconds: any;
  private banner;
  private banners;


  public constructor(
    private _http: HttpClient,
    public authService: AuthService,
    private paramService: ParamService
  ) {
    this.banners = Object.keys(environment.bannerConfiguration);
    this.banner = paramService.resolveBanner() || this.windowObj['AB']['userInfo']['banner'];
    this.enableBonusPathV2Flag =
      this.windowObj && this.windowObj["SWY"] &&
        this.windowObj["SWY"]["CONFIGSERVICE"] &&
        this.windowObj["SWY"]["CONFIGSERVICE"]["featureToggleConfig"] &&
        this.windowObj["SWY"]["CONFIGSERVICE"]["featureToggleConfig"][
        "disableContinuityOfferV2Flag"
        ]
        ? false
        : true;
  }

  /**
   * Get offers from my list
   *
   * @returns {Observable<any>} Observer with data containing offers
   */
  public getData(): Observable<any> {
    this.currentTimeInMilliseconds = Date.now();

    let httpOptions = {
      headers: new HttpHeaders(this.AB.j4uHttpOptions11)
        .set('X-Service-Name', 'MyListService')
        .set('X-Function-Name', 'getData')
        .set('X-SWY_VERSION', '4.0'),
      withCredentials: true,
    };
    const url = this.getDataUrl() + '?storeId=' + (this.AB.userInfo.j4u?.storeId || this.AB.userInfo.storeId) + '&_ws_as_ff=y&' + this.currentTimeInMilliseconds
    return this._http.get(url, httpOptions);
  }

  public getDataUrl() {
    return this.authService.getApplicationDomainURL() + this.AB.myListPath;
  }

  /**
   * Get offers and do custom logic on them.
   *
   * @param data     Data with offers from api
   * @param detailPath Detail path without offer id
   */
  public getOffers(data: any): CouponModel[] {
    // Generate detail path for "View details" link.
    let offerDetailPath = this.AB['userInfo']['siteType'] === 'B' ? this.AB['htmlOfferDetailPathB2b'] : this.AB['htmlOfferDetailPathWww']; // this._appConfigService.getCouponDetailPathWww();
    let rewardsDetailPath = this.authService.getApplicationDomainURL() + '/rewards/point-reward-details?myList=true';
    let offers = data.shoppingLists[0].items;

    if (offers) {
      offers = offers.map(
        (o: {
          itemType: string;
          detailPath: string;
          referenceId: string;
          offerTs: string;
          description: string;
          longTitle: any;
          summary: string;
          title: string;
          savings: any;
          wssalePrice: any;
          savingsValue: any;
          imageUrl: any;
          image: any;
          offerProtoType?: any;
        }) => {
          // o.endDate = o.endDate : new Date(o.endDate).getTime();
          //map the detail path based on offer type and id
          let detailPath;
          if (o.itemType === 'GR') {
            detailPath = rewardsDetailPath;
            o.detailPath = detailPath + '&rewardId=' + o.referenceId + '&rewardOfferType=' + o.itemType;
          } else {
            detailPath = offerDetailPath;
            o.detailPath = detailPath + '.' + o.referenceId;
            if (o.offerTs) {
              o.detailPath += '.' + o.offerTs;
            } else {
              o.detailPath += "." + Date.now();   // use current time stamp when none provided
            }
            o.detailPath += '.' + o.itemType;
            o.detailPath += '.html';
          }
          //truncate the description for the display of FF items
          if (o.itemType === 'FF') {
            let desc = o.description.replace(/(<([^>]+)>)/gi, ''); // NS-46393 remove HTML tags from FreeForm Offers
            let splitDesc = desc.split('%3F');
            o.description = splitDesc[0];
          }
          o.longTitle = o.summary ? o.title + ' ' + o.summary : o.title;
          o.savings = o.itemType == 'WS' ? o.wssalePrice : o.savingsValue;
          o.imageUrl = o.itemType == 'WS' && (!o.offerProtoType || o.offerProtoType !== "CMS") ? o.image : this.getImageUrl(o.image);

          return o;
        }
      );
    }
    return offers;
  }

  public getImageUrl(imageId: any): string {
    let productCardUrl = this.windowObj['AB']['dMTProductCard'] || 'images.albertsons-media.com/is/image/ABSStage/${product.id}?$ecom-product-card-${viewport}-jpg$&defaultImage=Not_Available'
    return productCardUrl
      .replace('${product.id}', imageId)
      .replace('${viewport}', 'mobile');
  }

  public getFilters(data: any): any[] {
    return data.shoppingLists[0].hierarchies.categories;
  }

  public sortFilters(filters: any[]): any[] {
    filters = filters.sort(
      (a: { [x: string]: number }, b: { [x: string]: number }) => {
        return a['name'] < b['name'] ? -1 : a['name'] > b['name'] ? 1 : 0;
      }
    );
    let specialFilters = filters.filter(
      (filter: { name: string }) => filter.name === 'Special Offers'
    );
    let withoutSpecialFilters = filters.filter(
      (filter: { name: string }) => filter.name !== 'Special Offers'
    );
    if (specialFilters.length > 0) {
      filters = [...specialFilters, ...withoutSpecialFilters];
    }
    return filters;
  }

  public filteringOffers(offers: any[], filters: number[]) {
    if (filters.length > 0) {
      offers = offers.filter((offer: { categoryId: any }) =>
        filters.includes(Number(offer.categoryId))
      );
    } else {
      offers = offers;
    }
    return offers;
  }

  public initialOffersSource = new BehaviorSubject<any>([]);
  public offersSource = new BehaviorSubject<any>([]);
  public categoriesSource = new BehaviorSubject<any>([]);

  public getInitialOffersData = this.initialOffersSource.asObservable();
  public getOffersData = this.offersSource.asObservable();
  public getCategories = this.categoriesSource.asObservable();

  public setInitialOffersData(offers: any) {
    this.initialOffersSource.next(offers);
  }

  public setOffersData(offers: any) {
    this.offersSource.next(offers);
  }

  public setCategories(categories: any) {
    this.categoriesSource.next(categories);
  }

  private sortByValue = new BehaviorSubject<string>('categoryId');
  public getSortByValue = this.sortByValue.asObservable();
  public setSortByValue(val: string) {
    this.sortByValue.next(val);
  }

  private showPicturesForPrint = new BehaviorSubject<boolean>(true);
  public getShowPicturesValForPrint = this.showPicturesForPrint.asObservable();
  public setShowPicturesValForPrint(val: boolean) {
    this.showPicturesForPrint.next(val);
  }

  //J4U services -> move to common service

  /**
   * determine the correct icon to display base on freshPass, UDC, enableDefault or default
   *        let that = {
   *            "iconImage":                           when '', will default to DEFAULT image which is SVG in HTML
   *                                                   when "LastBeforeDefaultIconImage", will display image configured in /etc/design
   *                                                   when "FreshPassIconImage", will display freshpass image
   *            "logoSVGCouponItemImageEnable" :       flag to replace DEFAULT image
   *            "logoSVGCouponItemImage" :             image to replace DEFAULT image
   *            "freshPassEnable":                     flag to enable FreshPass image (Freshpass image is base on events)
   *            "freshPassCouponEventListener":        event which Freshpass listens to
   *            "events" :                             coupon events which is use to match Freshpass event
   *            "UDCDealsEnable":                      Flag to enable UDC
   *        };
   */
  public findIconToDisplay(that: any) {
    if (!that.iconImage) {
      that.iconImage = '';
    }
    if (that.logoSVGCouponItemImageEnable) {
      that.iconImage = 'LastBeforeDefaultIconImage';
    }
    if (
      that.freshPassEnable &&
      that.freshPassCouponEventListener &&
      that.freshPassCouponEventListener.length > 0
    ) {
      if (Array.isArray(that.events)) {
        let freshPassExist = false;
        for (let i in that.events) {
          if (
            that.freshPassCouponEventListener
              .map((e: string) => e.toLowerCase())
              .indexOf(that.events[i].toLowerCase()) !== -1
          ) {
            freshPassExist = true;
          } else {
            freshPassExist = false;
            break;
          }
        }
        if (freshPassExist) {
          that.iconImage = 'FreshPassIconImage'; //only display freshpass logo if ALL events are freshpass event
        }
      }
    }
    if (that.UDCDealsEnable) {
      //this will override freshpass event
      that.iconImage = 'UDCDealIconImage';
    }
  }

  public rewardProtoTypes = ['REWARDS_FLAT', 'REWARDS_ACCUMULATION'];
  public isRewardCoupon(coupon: any) {
    return (
      (coupon.offerProgramType &&
        this.rewardProtoTypes.includes(coupon.offerProgramType)) ||
      (coupon.offerProtoType &&
        this.rewardProtoTypes.includes(coupon.offerProtoType))
    );
  }

  public isJ4ULogoContainerEnabled =
    this.windowObj &&
      this.windowObj['SWY'] &&
      this.windowObj['SWY']['CONFIGSERVICE'] &&
      this.windowObj['SWY']['CONFIGSERVICE']['featureToggleConfig'] &&
      this.windowObj['SWY']['CONFIGSERVICE']['featureToggleConfig'][
      'disableJ4ULogoContainer'
      ]
      ? false
      : true;
  /**
   * convert JSON from AEM eg. {Emilie Thanksgiving},{Weekly Ad Coupons}
   * into String array ["emilie thanksgiving", "weekly ad coupons"] for easier comparison
   */
  public convertJsonArrayToStringArray(_jsonArray: string): string[] {
    let _resultArray: string[] = _jsonArray.split(/[{}]/);
    let _filtered = _resultArray
      .map((v) => v.toLowerCase())
      .filter((n) => n)
      .filter((item) => item !== ',');
    return _filtered;
  }

  public getLogoList() {
    let url = `${this.authService.getApplicationDomainURL()}/content/experience-fragments/xf/foru/logos/master.caas.json`;
    return this._http.get(url).pipe(
      map((data: any) => {
        const logos = data.root.responsivegrid.j4u_logos;
        const logoProps = {
          FreshPassIconImage: {
            logo: logos.freshPassLogo,
            altText: 'Just 4 U Freshpass logo',
          },
          LastBeforeDefaultIconImage: {
            logo: logos.forULogo,
            altText: 'For U logo',
          },
          RewardsIconImage: {
            logo: logos.rewardsLogo,
            altText: 'Rewards logo',
          },
          defaultIconImage: {
            logo: logos.defaultLogo,
            altText: 'Just 4 U logo',
          },
        };
        return logoProps;
      }),
      catchError(this.handleError)
    );
  }

  public handleError(errorResponse: HttpErrorResponse): any {
    if (!(errorResponse.error instanceof ErrorEvent) && errorResponse.error) {
      return throwError(() => new Error('error'));
    }
  }

  public couponUpdated = new Subject();

  public unclipCoupon(offer: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders(this.windowObj['AB'].j4uHttpOptions)
        .set('X-Service-Name', 'MyListService')
        .set('X-Function-Name', 'unclipCoupon'),
    };
    let unClipUrl = this.unclipCouponUrl();
    let result: Observable<any> = defer(() => of({}));

    const body = { items: [{ id: offer.id }] };

    result = this._http.post(unClipUrl, body, httpOptions).pipe(
      map(
        (response: any) => {
          this.updateLocalStorage('abJ4uCoupons', false, offer);

          this.couponUpdated.next({
            id: offer.referenceId,
            deleted: true,
            status: 'U',
          });
          this.unclipEcomGalleryCoupon(offer.referenceId);

          var _coupon = {
            brand: offer.title,
            offerType: offer.itemType,
            offerId: offer.referenceId,
            offerPrice: offer.savingsValue,
          };

          this.windowObj['AB']['DATALAYER'].unClipCoupon(_coupon);
          //add 'ecoupon' flag in cookie to show shop coupon has been unclipped
          this.windowObj['SWY'].ENFORCEMENTUTILS.updateReInitSite('ecoupon');

          return response;
        }),
      catchError((err) => {
        console.error('Unclip error', err);
        let strError = err.status + ': Coupon UnClip failed';
        if (
          typeof err.error == 'object' &&
          Array.isArray(err.error.errors) &&
          err.error.errors.length > 0
        ) {
          strError +=
            ' ' + err.error.errors[0].code + ' ' + err.error.errors[0].msg;
        }

        this.windowObj['AB']['DATALAYER'].setError(
          offer.id.toString(),
          'coupon clipping',
          strError
        );

        return throwError(err.error.message);
      })
    );
    return result;
  }

  public unclipCouponUrl() {
    return this.authService.getApplicationDomainURL() + this.windowObj['AB']['couponUnclipPath'];
  }

  public shopCouponRefreshSource = new BehaviorSubject<boolean>(false);
  public shopCouponRefresh = this.shopCouponRefreshSource.asObservable();

  public unclipEcomGalleryCoupon(couponId: any) {
    let objOffer = this.getOffer(couponId);

    if (this.authService.isLSCompatible() && localStorage && objOffer != '') {
      let absCouponItem = localStorage.getItem('abCoupons') || '';
      let allCoupons = JSON.parse(absCouponItem);
      if (allCoupons && allCoupons.offers && allCoupons.offers[couponId]) {
        allCoupons.offers[couponId].status = 'U';
        allCoupons.offers[couponId].deleted = true; //set deleted flag in localStorage
        this._set(allCoupons, false);
        this.shopCouponRefreshSource.next(true);
      }
    }
  }

  private objCoupons = {};

  private _set(coupons: any, setDate: boolean = true) {
    if (setDate) {
      coupons['updatedTime'] = JSON.stringify(new Date());
    }
    if (this.authService.isLSCompatible()) {
      localStorage.setItem('abCoupons', JSON.stringify(coupons));
    }
    this.objCoupons = coupons;
  }

  public getOffer(offerId: any) {
    let objOffers = '';
    let objCoupons = this._get();
    debugger
    if (objCoupons && objCoupons.offers && objCoupons.offers[offerId]) {
      objOffers = objCoupons.offers[offerId];
    }

    return objOffers;
  }

  private _get(): any {
    let objCoupons = this.objCoupons;
    if (this.authService.isLSCompatible() && Object.keys(objCoupons).length === 0) {
      let strCoupons = localStorage.getItem('abCoupons');
      try {
        if (strCoupons) {
          try {
            objCoupons = JSON.parse(strCoupons);
            this.objCoupons = objCoupons;
          } catch (e) { }
        }
      } catch (e) {
        console.error('ERROR: COUPON - GETTING FROM LOCAL STORAGE');
        console.error(e);
      }
    }
    return objCoupons;
  }

  private updateLocalStorage(
    localStorageItem: any,
    isClipped: boolean,
    coupon: any
  ) {
    if (this.authService.isLSCompatible() && localStorage[localStorageItem] != undefined) {
      let couponsData = JSON.parse(localStorage[localStorageItem]);
      if (couponsData) {
        couponsData['updatedOn'] = new Date().toLocaleString();
        let objCoupons = couponsData['objCoupons'];
        if (
          objCoupons &&
          objCoupons[isClipped ? coupon.offerId : coupon.referenceId] !=
          undefined
        ) {
          let tempCoupon =
            objCoupons[isClipped ? coupon.offerId : coupon.referenceId];
          tempCoupon.status = isClipped ? 'C' : 'U';
          tempCoupon.deleted = !isClipped;
          /* custom code for instant allocation
           * can be deleted once campaign is completed */
          if (tempCoupon.instantAllocation == true && isClipped) {
            couponsData['instantAllocOffer'] = [];
            tempCoupon.instantAllocation = undefined;
          } else if (
            tempCoupon.events.includes('Welcome Offers') &&
            !isClipped
          ) {
            tempCoupon.instantAllocation = true;
            couponsData['instantAllocOffer'] = [tempCoupon];
          }
        }
        couponsData['arrClippedCoupons'] = isClipped
          ? [...couponsData['arrClippedCoupons'], coupon.offerId]
          : couponsData['arrClippedCoupons'].filter(
            (id: any) => id != coupon.referenceId
          );
        localStorage[localStorageItem] = JSON.stringify(couponsData);
      }
    }
  }

  //continuityOfferService


  public continuityOfferType: string = "continuity";

  public isBehaviourOrContinuityCoupon(coupon: any): boolean {
    let flag = false;
    if (
      (coupon.offerProgramType && coupon.offerProgramType !== "") ||
      (coupon.offerProtoType && coupon.offerProtoType !== "")
    ) {
      if (
        coupon.offerProgramType &&
        coupon.offerProgramType.toLowerCase() ===
        this.continuityOfferType.toLowerCase()
      ) {
        flag = true;
      } else if (
        coupon.offerProtoType &&
        coupon.offerProtoType.toLowerCase() ===
        this.continuityOfferType.toLowerCase()
      ) {
        flag = true;
      }
      else if (
        coupon.qualificationBehavior &&
        coupon.qualificationBehavior !== ""
      ) {
        flag = true;
      }
    }
    return flag;
  }

  public fetchMyListDisclaimer() {
    const url = this.authService.getApplicationDomainURL() + environment.myListDisclaimerContent;
    const headers = new HttpHeaders().set('X-Service-Name', 'MyListService').set('X-Function-Name', 'fetchMyListDisclaimer');
    return this._http.get(url, { headers, responseType: 'text' }).pipe(
      map((data: any) => {
        if (data) {
          data = data.replace(/\(displayableBanner\)/g, this.windowObj['AB'].userInfo.displayableBanner);
          data = data.replace(/\(banner\)/g, this.windowObj['AB'].userInfo.banner);
          data = data.replace(/\(banner4U\)/g, this.windowObj['AB'].userInfo.displayableBanner);
          data = data.replace(/\(bannerName\)/g, this.windowObj['AB'].userInfo.displayableBanner);
          return data;
        }
      }
      ));
  }

  public getMyListHTMLProperties(): any {
    const aemPropsEle = document.getElementById('aemMyListProps');
    const aemMyListProps = aemPropsEle?.getAttribute('data-aem-config');

    if (!aemMyListProps) {
      return undefined;
    }

    try {
      const parsedJSONData = JSON.parse(aemMyListProps);
      return parsedJSONData?.myListProps?.bannerProps[this.banner];
    } catch (e) {
      console.error('Failed to parse aemMyListProps', e);
      return undefined;
    }
  }
}

